import { useState } from "react";
import {
  Loader2,
  ArrowDownToLine,
  ArrowLeftRight,
  History,
  CheckCircle2,
  ArrowUpRight,
  ArrowDownLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import {
  requestCashbackWithdrawal,
  transferCashbackToReferral,
} from "@/lib/cashbackFunctions";
import { useWithdrawalHistory, useTransferHistory } from "@/hooks/useCashback";
import { cn } from "@/lib/utils";

type Method = "bank_transfer" | "wallet";
type Tab = "withdraw" | "transfer" | "history";

function formatDate(ts: unknown): string {
  if (!ts) return "";
  try {
    const date =
      typeof (ts as { toDate?: () => Date }).toDate === "function"
        ? (ts as { toDate: () => Date }).toDate()
        : new Date(ts as string);
    return date.toLocaleDateString("ar-EG-u-ca-gregory", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function CashbackWithdrawTransfer({
  availableBalance,
  currencySymbol = "ر.س",
  userId,
}: {
  availableBalance: number;
  currencySymbol?: string;
  userId: string | null;
}) {
  const [tab, setTab] = useState<Tab>("withdraw");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [wAmount, setWAmount] = useState("");
  const [wMethod, setWMethod] = useState<Method>("bank_transfer");
  const [wDetails, setWDetails] = useState("");

  const [tAmount, setTAmount] = useState("");
  const [tCode, setTCode] = useState("");

  const { withdrawals, loading: wLoading } = useWithdrawalHistory(
    tab === "history" ? userId : null
  );
  const { transfers, loading: tLoading } = useTransferHistory(
    tab === "history" ? userId : null
  );

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const amount = Number(wAmount.replace(/,/g, "."));
    if (!Number.isFinite(amount) || amount < 1) {
      setError("أدخل مبلغاً صحيحاً");
      return;
    }
    if (amount > availableBalance + 1e-6) {
      setError("المبلغ أكبر من الرصيد المتاح");
      return;
    }
    if (wDetails.trim().length < 5) {
      setError("أدخل بيانات التحويل (اسم البنك / آيبان / محفظة)");
      return;
    }
    setLoading(true);
    try {
      await requestCashbackWithdrawal({
        amount,
        method: wMethod,
        details: wDetails.trim(),
      });
      toast({
        title: "تم السحب بنجاح",
        description: `تم خصم ${amount.toFixed(2)} ${currencySymbol} من رصيدك.`,
      });
      setWAmount("");
      setWDetails("");
    } catch (err: unknown) {
      setError(
        (err as { message?: string })?.message || "تعذر تنفيذ الطلب. حاول لاحقاً."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const amount = Number(tAmount.replace(/,/g, "."));
    if (!Number.isFinite(amount) || amount < 1) {
      setError("أدخل مبلغاً صحيحاً");
      return;
    }
    if (amount > availableBalance + 1e-6) {
      setError("المبلغ أكبر من الرصيد المتاح");
      return;
    }
    if (tCode.trim().length < 3) {
      setError("أدخل كود الإحالة");
      return;
    }
    setLoading(true);
    try {
      await transferCashbackToReferral({
        toReferralCode: tCode.trim(),
        amount,
      });
      toast({
        title: "تم التحويل",
        description: "تم تحويل المبلغ إلى المستخدم الآخر.",
      });
      setTAmount("");
      setTCode("");
    } catch (err: unknown) {
      setError(
        (err as { message?: string })?.message ||
          "تعذر التحويل. تحقق من الكود والرصيد."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-4 mb-6">
      <Card className="border-2">
        <CardHeader className="pb-2">
          <div className="flex gap-1 p-1 bg-muted rounded-lg">
            {(
              [
                { key: "withdraw", label: "سحب", icon: ArrowDownToLine },
                { key: "transfer", label: "تحويل", icon: ArrowLeftRight },
                { key: "history", label: "السجل", icon: History },
              ] as { key: Tab; label: string; icon: React.ElementType }[]
            ).map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setTab(key);
                  setError(null);
                }}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-colors",
                  tab === key
                    ? "bg-background text-foreground shadow"
                    : "text-muted-foreground"
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent className="space-y-4 pt-2">
          {/* ── Withdraw ── */}
          {tab === "withdraw" && (
            <>
              <p className="text-xs text-muted-foreground">
                الرصيد المتاح:{" "}
                <span className="font-semibold text-foreground tabular-nums">
                  {availableBalance.toFixed(2)} {currencySymbol}
                </span>
              </p>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <form onSubmit={handleWithdraw} className="space-y-3">
                <div className="space-y-2">
                  <Label>المبلغ ({currencySymbol})</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={wAmount}
                    onChange={(e) => setWAmount(e.target.value)}
                    placeholder="0.00"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label>طريقة الاستلام</Label>
                  <Select
                    value={wMethod}
                    onValueChange={(v) => setWMethod(v as Method)}
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                      <SelectItem value="wallet">محفظة إلكترونية</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>بيانات التحويل</Label>
                  <Input
                    value={wDetails}
                    onChange={(e) => setWDetails(e.target.value)}
                    placeholder="اسم البنك، رقم الآيبان، أو رقم المحفظة"
                    disabled={loading}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-xl"
                  disabled={loading || availableBalance < 1}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    "تأكيد السحب"
                  )}
                </Button>
              </form>
            </>
          )}

          {/* ── Transfer ── */}
          {tab === "transfer" && (
            <>
              <p className="text-xs text-muted-foreground">
                الرصيد المتاح:{" "}
                <span className="font-semibold text-foreground tabular-nums">
                  {availableBalance.toFixed(2)} {currencySymbol}
                </span>
              </p>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <form onSubmit={handleTransfer} className="space-y-3">
                <div className="space-y-2">
                  <Label>كود الإحالة للمستلم</Label>
                  <Input
                    value={tCode}
                    onChange={(e) => setTCode(e.target.value.toUpperCase())}
                    placeholder="REFXXXXX"
                    className="font-mono"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label>المبلغ ({currencySymbol})</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={tAmount}
                    onChange={(e) => setTAmount(e.target.value)}
                    placeholder="0.00"
                    disabled={loading}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-xl"
                  disabled={loading || availableBalance < 1}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    "تأكيد التحويل"
                  )}
                </Button>
              </form>
            </>
          )}

          {/* ── History ── */}
          {tab === "history" && (
            <div className="space-y-4">
              {/* Withdrawal history */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  طلبات السحب
                </p>
                {wLoading ? (
                  <div className="space-y-2">
                    {[0, 1].map((i) => (
                      <Skeleton key={i} className="h-14 rounded-xl" />
                    ))}
                  </div>
                ) : withdrawals.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    لا توجد طلبات سحب بعد
                  </p>
                ) : (
                  <div className="space-y-2">
                    {withdrawals.map((w) => (
                      <div
                        key={w.id}
                        className="flex items-center gap-3 bg-secondary/50 rounded-xl px-3 py-2.5"
                      >
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">
                            {w.method === "bank_transfer"
                              ? "تحويل بنكي"
                              : "محفظة إلكترونية"}
                          </p>
                          <p className="text-[10px] text-muted-foreground truncate">
                            {w.details}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {formatDate(w.createdAt)}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold text-foreground tabular-nums">
                            {(w.amount ?? 0).toFixed(2)}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {w.currencyCode ?? currencySymbol}
                          </p>
                          <span className="text-[10px] font-medium text-emerald-600">
                            مكتمل
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Transfer history */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  التحويلات
                </p>
                {tLoading ? (
                  <div className="space-y-2">
                    {[0, 1].map((i) => (
                      <Skeleton key={i} className="h-14 rounded-xl" />
                    ))}
                  </div>
                ) : transfers.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    لا توجد تحويلات بعد
                  </p>
                ) : (
                  <div className="space-y-2">
                    {transfers.map((t) => {
                      const isOut = t.type === "out";
                      return (
                        <div
                          key={t.id}
                          className="flex items-center gap-3 bg-secondary/50 rounded-xl px-3 py-2.5"
                        >
                          <div
                            className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                              isOut ? "bg-red-100" : "bg-emerald-100"
                            )}
                          >
                            {isOut ? (
                              <ArrowUpRight className="w-4 h-4 text-red-500" />
                            ) : (
                              <ArrowDownLeft className="w-4 h-4 text-emerald-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground">
                              {isOut ? "تحويل صادر" : "تحويل وارد"}
                            </p>
                            {t.counterpartyReferralCode && (
                              <p className="text-[10px] text-muted-foreground font-mono">
                                {t.counterpartyReferralCode}
                              </p>
                            )}
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {formatDate(t.createdAt)}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p
                              className={cn(
                                "text-sm font-bold tabular-nums",
                                isOut ? "text-red-500" : "text-emerald-600"
                              )}
                            >
                            {isOut ? "-" : "+"}
                            {(t.amount ?? 0).toFixed(2)}
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {t.currencyCode ?? currencySymbol}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
